import React from 'react';
import BaseEChart from './BaseEChart';
import {
  CHART_AXIS_LABEL_STYLE,
  CHART_COLORS,
  CHART_FONT_FAMILY,
} from './chartConstants';
import { PLACEHOLDER } from '@utils/format';
import type { ChartOption } from '@app-types/chart';

export interface ProgressGaugeChartProps {
  /** Raw API progress percentage; display may exceed 100, visual fill is capped. */
  percent: number | null;
  /** Center label, e.g. "2Q". */
  centerLabel: string | null;
  height?: number;
}

const GAUGE_HEIGHT = 108.37;
const GAUGE_CENTER: [number, number] = [89.87, 89.87];
const GAUGE_START_ANGLE = Math.PI;
const GAUGE_END_ANGLE = Math.PI * 2;
const GAUGE_OUTER_RADIUS = 89.37;
const GAUGE_INNER_RADIUS = 53.87;
const GAUGE_FILL_OUTER_RADIUS = 88.87;
const GAUGE_FILL_INNER_RADIUS = 54.37;
// Anchor the combined value/unit rich text directly to the gauge center.
// ECharts graphic text does not center like a CSS block from `left: 0` + width.
const GAUGE_VALUE_TOP = 79.37;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const clampPercent = (value: number | null) =>
  isFiniteNumber(value) ? Math.max(0, Math.min(100, value)) : 0;

const formatGaugePercent = (value: number | null) =>
  isFiniteNumber(value)
    ? value.toLocaleString('ja-JP', {
        maximumFractionDigits: 1,
      })
    : PLACEHOLDER;

/**
 * Pure presentation model for the gauge. Keeps the capped visual fill separate
 * from the raw API value shown as text (which may exceed 100).
 */
export interface ProgressGaugePresentation {
  /** Visual fill percentage, clamped to 0-100. */
  visualPercent: number;
  /** Text drawn at the gauge center, e.g. "232.7" or the no-data placeholder. */
  displayValue: string;
  /** Whether the percent unit ("%") should be rendered next to the value. */
  showPercentUnit: boolean;
}

export const buildProgressGaugePresentation = (
  percent: number | null,
): ProgressGaugePresentation => ({
  visualPercent: clampPercent(percent),
  displayValue: formatGaugePercent(percent),
  showPercentUnit: isFiniteNumber(percent),
});

/** Accessible summary based on the raw API value, never the capped fill. */
export const buildProgressGaugeAriaLabel = (
  percent: number | null,
  centerLabel: string | null,
): string => {
  const hasPercent = isFiniteNumber(percent);
  return `進捗ゲージ ${centerLabel ?? PLACEHOLDER} ${formatGaugePercent(percent)}${
    hasPercent ? '%' : ''
  }`;
};

/**
 * Pure ECharts option builder for the `今期進捗状況` progress dial
 * (Figma 3174:14212). Exported so it can be unit-tested without mounting
 * ECharts through BaseEChart.
 */
export const buildProgressGaugeOption = (
  percent: number | null,
  centerLabel: string | null,
): ChartOption => {
  const { visualPercent, displayValue, showPercentUnit } =
    buildProgressGaugePresentation(percent);
  const progressRatio = visualPercent / 100;
  const progressAngle =
    GAUGE_START_ANGLE + (GAUGE_END_ANGLE - GAUGE_START_ANGLE) * progressRatio;
  const progressBoundary =
    visualPercent > 0 && visualPercent < 100
      ? {
          type: 'line',
          z: 4,
          silent: true,
          shape: {
            x1:
              GAUGE_CENTER[0] +
              GAUGE_FILL_INNER_RADIUS * Math.cos(progressAngle),
            y1:
              GAUGE_CENTER[1] +
              GAUGE_FILL_INNER_RADIUS * Math.sin(progressAngle),
            x2:
              GAUGE_CENTER[0] +
              GAUGE_FILL_OUTER_RADIUS * Math.cos(progressAngle),
            y2:
              GAUGE_CENTER[1] +
              GAUGE_FILL_OUTER_RADIUS * Math.sin(progressAngle),
          },
          style: {
            stroke: '#999999',
            lineWidth: 1,
          },
        }
      : null;
  const progressFill =
    visualPercent > 0
      ? {
          type: 'sector',
          z: 3,
          silent: true,
          shape: {
            cx: GAUGE_CENTER[0],
            cy: GAUGE_CENTER[1],
            r: GAUGE_FILL_OUTER_RADIUS,
            r0: GAUGE_FILL_INNER_RADIUS,
            startAngle: GAUGE_START_ANGLE,
            endAngle: progressAngle,
            clockwise: true,
          },
          style: {
            fill: CHART_COLORS.seriesPrimary,
            stroke: 'none',
          },
        }
      : null;

  // Single rich-text node: the numeric value keeps its 24px weight and the
  // "%" unit keeps its 18px weight, but they now share one centered layout box
  // so wide values (e.g. "232.7%") never collide with a fixed "%" coordinate.
  const progressText = showPercentUnit
    ? `{value|${displayValue}}{unit|%}`
    : `{value|${displayValue}}`;

  const option: ChartOption = {
    tooltip: { show: false },
    graphic: {
      elements: [
        {
          type: 'sector',
          z: 1,
          silent: true,
          shape: {
            cx: GAUGE_CENTER[0],
            cy: GAUGE_CENTER[1],
            r: GAUGE_OUTER_RADIUS,
            r0: GAUGE_INNER_RADIUS,
            startAngle: GAUGE_START_ANGLE,
            endAngle: GAUGE_END_ANGLE,
            clockwise: true,
          },
          style: {
            fill: '#ffffff',
            stroke: '#999999',
            lineWidth: 1,
          },
        },
        ...(progressFill ? [progressFill] : []),
        ...(progressBoundary ? [progressBoundary] : []),
        {
          type: 'text',
          left: 77.37,
          top: 55.37,
          z: 10,
          silent: true,
          style: {
            text: centerLabel ?? PLACEHOLDER,
            width: 25,
            height: 22,
            align: 'center',
            verticalAlign: 'top',
            // v20 refresh (§5.4): gauge center text uses '#484848'.
            fill: '#484848',
            fontFamily: CHART_FONT_FAMILY,
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: -0.09,
          },
        },
        {
          type: 'text',
          z: 10,
          silent: true,
          style: {
            text: progressText,
            x: GAUGE_CENTER[0],
            y: GAUGE_VALUE_TOP,
            align: 'center',
            verticalAlign: 'top',
            rich: ({
              value: {
                // v20 refresh (§5.4): gauge value uses '#484848'.
                fill: '#484848',
                fontFamily: CHART_FONT_FAMILY,
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: -0.24,
              },
              unit: {
                // v20 refresh (§5.4): gauge percent uses '#484848'.
                fill: '#484848',
                fontFamily: CHART_FONT_FAMILY,
                fontSize: 18,
                fontWeight: 700,
                padding: [5, 0, 0, 2],
                letterSpacing: -0.09,
              },
            } as any),
          },
        },
        {
          type: 'text',
          left: 14.53,
          top: 94.37,
          z: 10,
          silent: true,
          style: {
            text: '0%',
            width: 18,
            height: 14,
            align: 'center',
            verticalAlign: 'top',
            fill: CHART_COLORS.axisLabel,
            ...(CHART_AXIS_LABEL_STYLE as any),
          },
        },
        {
          type: 'text',
          left: 144.32,
          top: 94.37,
          z: 10,
          silent: true,
          style: {
            text: '100%',
            width: 31,
            height: 14,
            align: 'center',
            verticalAlign: 'top',
            fill: CHART_COLORS.axisLabel,
            ...(CHART_AXIS_LABEL_STYLE as any),
          },
        },
      ],
    },
  };

  return option;
};

/** ECharts version of the Figma 3174:14212 progress-rate dial. */
const ProgressGaugeChart: React.FC<ProgressGaugeChartProps> = ({
  percent,
  centerLabel,
  height = GAUGE_HEIGHT,
}) => (
  <BaseEChart
    option={buildProgressGaugeOption(percent, centerLabel)}
    height={height}
    className="progress-gauge-chart"
    ariaLabel={buildProgressGaugeAriaLabel(percent, centerLabel)}
  />
);

export default ProgressGaugeChart;
